// ANALYTICS-01 basic KPIs (Today, 7 days, 30 days in NPT) and the "needs you" queue counts.
// Exported for the Today page (lead mounts it; IA §7.1) and shown on the Orders page header.
import { useState } from "react";
import { t } from "@ady/i18n";
import { BusyRegion, EmptyState, Skeleton, Tabs } from "@ady/ui";
import { LoadError } from "../settings/SettingsPage";
import { useKpis, type Kpis } from "./api";
import { Card, money, useCan, useStoreIds } from "./ui";
import { CTID } from "./testids";

function Stat({
  label,
  value,
  hint,
  testId,
}: {
  label: string;
  value: string;
  hint?: string;
  testId?: string;
}) {
  return (
    <div
      className="flex min-w-0 flex-col gap-0.5 rounded-sm bg-sunken px-3 py-2.5"
      data-testid={testId}
    >
      <span className="text-xs text-fg-secondary">{label}</span>
      <span className="ady-ledger text-title-sm font-semibold">{value}</span>
      {hint ? <span className="text-2xs text-fg-tertiary">{hint}</span> : null}
    </div>
  );
}

export function TodayKpis() {
  const { orgId, storeId } = useStoreIds();
  const allowed = useCan("reports.read");
  const [period, setPeriod] = useState<Kpis["period"]>("today");
  const q = useKpis(orgId, storeId, period, allowed);
  if (!allowed) return null;
  const k = q.data;
  const body = (
    <>
      {q.isError ? (
        <LoadError error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <BusyRegion busy={q.isPending} skeleton={<Skeleton className="h-24 w-full" />}>
          {k && k.orders === 0 && k.queue.to_confirm === 0 ? (
            <EmptyState
              size="inline"
              headingLevel={3}
              title={t("commerce.today.emptyTitle")}
              body={t("commerce.today.emptyBody")}
            />
          ) : k ? (
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
                <Stat
                  label={t("commerce.today.orders")}
                  value={String(k.orders)}
                  hint={t("commerce.today.previous", { value: String(k.previous.orders) })}
                  testId={CTID.kpiOrders}
                />
                <Stat
                  label={t("commerce.today.sales")}
                  value={money(k.sales_paisa)}
                  hint={t("commerce.today.previous", { value: money(k.previous.sales_paisa) })}
                  testId={CTID.kpiSales}
                />
                <Stat label={t("commerce.today.average")} value={money(k.average_order_paisa)} />
                <Stat label={t("commerce.today.collected")} value={money(k.collected_paisa)} />
                <Stat label={t("commerce.today.outstanding")} value={money(k.outstanding_paisa)} />
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-fg-secondary">
                <span>
                  {t("commerce.today.queueItem", {
                    label: t("commerce.today.toConfirm"),
                    count: k.queue.to_confirm,
                  })}
                </span>
                <span>
                  {t("commerce.today.queueItem", {
                    label: t("commerce.today.toCheck"),
                    count: k.queue.awaiting_verification,
                  })}
                </span>
                <span>
                  {t("commerce.today.queueItem", {
                    label: t("commerce.today.late"),
                    count: k.queue.unfulfilled_late,
                  })}
                </span>
                <span>
                  {t("commerce.today.queueItem", {
                    label: t("commerce.today.unconfirmedCod"),
                    count: k.queue.unconfirmed_cod,
                  })}
                </span>
              </div>
            </div>
          ) : null}
        </BusyRegion>
      )}
    </>
  );
  return (
    <Card id="commerce-kpis" title={t("commerce.today.title")}>
      <Tabs
        label={t("commerce.today.periodLabel")}
        value={period}
        onValueChange={(v) => setPeriod(v as Kpis["period"])}
        items={(["today", "7d", "30d"] as const).map((p) => ({
          id: p,
          label: t(`commerce.today.periods.${p}`),
          panel: p === period ? body : null,
        }))}
        data-testid={CTID.kpis}
      />
    </Card>
  );
}
